import React, { useState, useEffect } from 'react';
import { ArrowLeft, Timer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';

interface SquareTargetGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

const SquareTargetGame: React.FC<SquareTargetGameProps> = ({ mode, onReset, language }) => {
  const [numbers, setNumbers] = useState<number[]>([]);
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [remainingTime, setRemainingTime] = useState(30);
  const [gameOver, setGameOver] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState<{[key: number]: boolean}>({});
  const [feedback, setFeedback] = useState<{[key: number]: 'correct' | 'incorrect'}>({});
  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  useEffect(() => {
    generateNumbers();
    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          setGameOver(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);    
    return () => clearInterval(timer);
  }, [level]);

  useEffect(() => {
    if (gameOver) {
      playSuccess();
    }
  }, [gameOver]);
  
  const generateNumbers = () => {
    const maxRoot = level === 1 ? 6 : level === 2 ? 8 : 10;
    const count = 8;
    const squares = [];
    const nonSquares = [];

    // Generate square numbers
    while (squares.length < count / 2) {
      const root = Math.floor(Math.random() * maxRoot) + 1;
      const square = root * root;
      if (!squares.includes(square)) {
        squares.push(square);
      }
    }

    // Generate non-square numbers
    while (nonSquares.length < count / 2) {
      const num = Math.floor(Math.random() * (maxRoot * maxRoot));
      if (!squares.includes(num) && !nonSquares.includes(num) && !Number.isInteger(Math.sqrt(num))) {
        nonSquares.push(num);
      }
    }

    setNumbers([...squares, ...nonSquares].sort(() => Math.random() - 0.5));
    setSelectedNumbers({});
    setFeedback({});
  };

  const handleNumberClick = async (number: number) => {
    if (gameOver || selectedNumbers[number]) return;

    const isSquare = Number.isInteger(Math.sqrt(number));
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (isSquare ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (isSquare ? 0 : 1),
    };
    setStats(newStats);

    setSelectedNumbers(prev => ({ ...prev, [number]: true }));
    setFeedback(prev => ({ ...prev, [number]: isSquare ? 'correct' : 'incorrect' }));

    if (isSquare) {
      setScore(score + 1);
      
      const remainingSquares = numbers.filter(n => 
        !selectedNumbers[n] && Number.isInteger(Math.sqrt(n))
      ).length;

      if (remainingSquares <= 1) {
        if (level < 3) {
          setTimeout(() => {
            setLevel(level + 1);
            generateNumbers();
          }, 1000);
        } else {
          setGameOver(true);
          saveProgress(newStats);
        }
      }
    } else {
      setScore(Math.max(0, score - 1));
    }
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'square',
        mode: 'target',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 30 - remainingTime,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const getEndGameMessage = () => {
    if (language === 'en') {
      if (score >= 10) return 'Outstanding! You\'re a Square Number Master!';
      if (score >= 7) return 'Great job! You\'re getting really good at this!';
      if (score >= 4) return 'Good effort! Keep practicing to improve!';
      return 'Keep trying! Square numbers will become easier with practice.';
    } else {
      if (score >= 10) return 'Exceptionnel ! Tu es un Maître des Nombres Carrés !';
      if (score >= 7) return 'Excellent travail ! Tu deviens vraiment bon !';
      if (score >= 4) return 'Bon effort ! Continue de pratiquer pour t\'améliorer !';
      return 'Continue d\'essayer ! Les nombres carrés deviendront plus faciles avec la pratique.';
    }
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 ${
            remainingTime <= 10 
              ? 'text-red-600 dark:text-red-400' 
              : 'text-indigo-600 dark:text-indigo-400'
          }`}>
            <Timer className="h-5 w-5" />
            <span className="font-bold">{remainingTime}s</span>
          </div>
          <div className="text-lg font-medium text-indigo-700 dark:text-indigo-300">
            {language === 'en' ? `Level ${level}` : `Niveau ${level}`}
          </div>
        </div>
      </div>

      {gameOver ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {getEndGameMessage()}
          </h2>
          <div className="mb-8">
            <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
              {score}
            </div>
            <div className="text-slate-600 dark:text-slate-300">
              {language === 'en' ? 'Final Score' : 'Score final'}
            </div>
          </div>
          <button
            onClick={() => {
              setLevel(1);
              setScore(0);
              setGameOver(false);
              setRemainingTime(30);
              generateNumbers();
              setStats({
                questionsAnswered: 0,
                correctAnswers: 0,
                incorrectAnswers: 0,
                totalTime: 0,
                averageResponseTime: 0,
              });
            }}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
              hover:bg-indigo-700 dark:hover:bg-indigo-600 
              focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 text-center">
              {language === 'en' 
                ? 'Click on the perfect square numbers'
                : 'Cliquez sur les nombres carrés parfaits'}
            </h2>
            <p className="text-center text-slate-600 dark:text-slate-300">
              {language === 'en'
                ? '+1 point for each correct square, -1 for mistakes'
                : '+1 point pour chaque carré correct, -1 pour les erreurs'}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {numbers.map((number) => {
              const feedbackState = feedback[number];
              return (
                <button
                  key={number}
                  onClick={() => handleNumberClick(number)}
                  disabled={selectedNumbers[number] || gameOver}
                  className={`aspect-square rounded-lg text-2xl font-bold transition-all duration-300
                    ${feedbackState === 'correct'
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-2 border-green-500'
                      : feedbackState === 'incorrect'
                      ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-2 border-red-500'
                      : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-300'
                    }`}
                >
                  {number}
                </button>
              );
            })}
          </div>

          <div className="text-center text-xl font-semibold text-indigo-600 dark:text-indigo-400">
            {language === 'en' ? 'Score: ' : 'Score : '}{score}
          </div>
        </div>
      )}
    </div>
  );
};

export default SquareTargetGame;